/**
 * The notice a failed handoff leaves on the store (D385): the shopper stays on the page, and a
 * retry is one tap away. Shopify's checkout is never the way out (`./outcome.ts`).
 *
 * Small on purpose: every byte is served on every storefront page view. No raw error code is shown
 * (the reason goes to the console and the fallback report). Inline styles set through CSSOM, which
 * a shop's Content-Security-Policy leaves alone, and `all:initial` so a theme's `button` rules do
 * not reach in.
 *
 * Attached to `document.body` with the highest `z-index` there is, after everything else, so it
 * sits above a cart drawer, UpCart's shadow host included. Two overlays no `z-index` can beat:
 * a modal `<dialog>` keeps everything outside it inert, so an open one hosts the notice instead; and
 * the top layer, which `popover` enters where the browser has it.
 */

/** Message, retry label, close label. Keyed by the `<html lang>`'s language; `en` is the default. */
const TEXTS: Record<string, readonly [string, string, string]> = {
  en: ['The checkout could not be opened.', 'Try again', 'Close'],
  nl: ['De checkout kon niet worden geopend.', 'Opnieuw proberen', 'Sluiten'],
  de: ['Die Kasse konnte nicht geöffnet werden.', 'Erneut versuchen', 'Schließen'],
  fr: ['Le paiement n’a pas pu s’ouvrir.', 'Réessayer', 'Fermer'],
  es: ['No se ha podido abrir el pago.', 'Reintentar', 'Cerrar'],
  it: ['Impossibile aprire il checkout.', 'Riprova', 'Chiudi'],
  da: ['Betalingen kunne ikke åbnes.', 'Prøv igen', 'Luk'],
};

/** The texts for a `lang` value: `nl`, `nl-NL`, `NL`; `en` for anything else. */
export function noticeTexts(lang: string): readonly [string, string, string] {
  return TEXTS[lang.slice(0, 2).toLowerCase()] ?? (TEXTS.en as readonly [string, string, string]);
}

let current: HTMLElement | null = null;

/** Take the notice off the page, if there is one. */
export function closeNotice(): void {
  if (current !== null) current.remove();
  current = null;
}

/** An open modal `<dialog>`, or `null`; a browser without `:modal` throws on the selector. */
function openModal(): Element | null {
  try {
    return document.querySelector('dialog:modal');
  } catch {
    return null;
  }
}

function button(label: string, css: string, act: () => void): HTMLButtonElement {
  const element = document.createElement('button');
  element.type = 'button';
  element.style.cssText = 'all:initial;cursor:pointer;font:inherit;color:inherit;' + css;
  element.textContent = label;
  element.addEventListener('click', act);
  return element;
}

/**
 * Show the notice, replacing one already there. "Try again" stays on screen, disabled, while
 * `retry` runs: the caller closes or replaces the notice when the attempt is over.
 */
export function showNotice(retry: () => void): void {
  closeNotice();
  const [message, again, close] = noticeTexts(document.documentElement.lang || '');

  const notice = document.createElement('div');
  notice.setAttribute('role', 'alertdialog');
  notice.setAttribute('aria-label', message);
  notice.setAttribute('data-peak-notice', '');
  notice.style.cssText =
    'all:initial;position:fixed;z-index:2147483647;inset:auto 16px 24px;margin:auto;' +
    'max-width:440px;display:flex;align-items:center;gap:12px;padding:12px 16px;' +
    'border-radius:8px;background:#fff;color:#111;box-shadow:0 4px 24px #0004;' +
    'font:14px/1.4 system-ui,sans-serif';

  const tryAgain = button(
    again,
    'margin-left:auto;font-weight:600;text-decoration:underline',
    () => {
      tryAgain.disabled = true;
      tryAgain.style.opacity = '.5';
      retry();
    },
  );
  const dismiss = button('×', 'font-size:18px', closeNotice);
  dismiss.setAttribute('aria-label', close);

  notice.append(message, tryAgain, dismiss);
  // So a theme's "click outside closes the drawer" does not take the drawer away underneath it.
  notice.addEventListener('click', (event) => event.stopPropagation());

  (openModal() ?? document.body).appendChild(notice);
  try {
    notice.popover = 'manual';
    notice.showPopover();
  } catch {
    // No popover support: the `z-index` above is what there is.
  }
  current = notice;
  tryAgain.focus({ preventScroll: true });
}
